import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Read from the `client_profitability` view (created by
 * 20260419100000_client_profitability_view.sql). Distinct from the existing
 * useClientProfitability hook — that one computes current-month metrics;
 * this one gives lifetime LTV + run-rate + deferred balance from the view.
 */
export interface ClientLTV {
  client_id: string;
  user_id: string;
  organization_id: string | null;
  client_name: string | null;
  avatar_url: string | null;
  client_status: string | null;
  client_created_at: string;
  lifetime_revenue: number;
  session_revenue: number;
  package_revenue: number;
  subscription_revenue: number;
  deferred_package_balance: number;
  completed_sessions: number;
  cancelled_sessions: number;
  avg_revenue_per_session: number;
  first_earning_at: string | null;
  last_earning_at: string | null;
  first_session_at: string | null;
  last_session_at: string | null;
  days_as_paying_client: number;
  monthly_run_rate: number;
}

export function useClientLTV(clientId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['client-ltv', user?.id, clientId],
    enabled: !!user?.id && !!clientId,
    staleTime: 60_000,
    queryFn: async (): Promise<ClientLTV | null> => {
      if (!user?.id || !clientId) return null;
      const { data, error } = await supabase
        .from('client_profitability')
        .select('*')
        .eq('client_id', clientId)
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return (data as ClientLTV) ?? null;
    },
  });
}

export function useTopClientsByLTV(options: {
  limit?: number;
  orderBy?: 'lifetime_revenue' | 'monthly_run_rate' | 'avg_revenue_per_session';
  minRevenue?: number;
} = {}) {
  const { limit = 10, orderBy = 'lifetime_revenue', minRevenue = 0 } = options;
  const { user } = useAuth();
  return useQuery({
    queryKey: ['client-ltv-top', user?.id, limit, orderBy, minRevenue],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<ClientLTV[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('client_profitability')
        .select('*')
        .eq('user_id', user.id)
        .gt('lifetime_revenue', minRevenue)
        .order(orderBy, { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data as ClientLTV[]) ?? [];
    },
  });
}
